import { useState } from "react";
import {
  Box, Typography, Paper, Stack, Button, Alert, TextField, MenuItem, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Table, TableBody, TableCell, TableHead, TableRow,
} from "@mui/material";
import { PageHeader } from "@/components/PageHeader";
import { StatusPill } from "@/components/StatusPill";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { diagnosticsApi, type DxSampleListDto } from "@/api/diagnostics";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { DiagnosticOrderIdField } from "@/components/DiagnosticOrderIdField";
import { formatDateTime } from "@/shared/datetime";

const STATUSES = ["", "Collected", "Received", "InProcess", "Rejected", "Resulted", "Disposed"];

export function DiagnosticSamplesPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canCollect = useAuth(s => s.hasPermission("diagnostics.sample.collect"));
  const [status, setStatus] = useState("");
  const [orderInput, setOrderInput] = useState("");
  // The field also accepts an accession number, so filter on the *resolved* order id, never the raw text.
  const [orderId, setOrderId] = useState<string | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ["dx-samples", orderId, status],
    queryFn: () => diagnosticsApi.listSamples(orderId ?? undefined, status || undefined),
  });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["dx-samples"] });
    qc.invalidateQueries({ queryKey: ["dx-collection-plan"] });
  };

  const receive = useMutation({
    meta: { successMessage: "Sample received in lab" },
    mutationFn: (id: string) => diagnosticsApi.receiveSample(id, {}),
    onSuccess: refresh,
  });
  // Received → InProcess. This is the transition that reports "Pending" back to HealNTrack.
  const startProcessing = useMutation({
    meta: { successMessage: "Sample under process — HealNTrack notified" },
    mutationFn: (id: string) => diagnosticsApi.startSampleProcessing(id, {}),
    onSuccess: refresh,
  });

  const busy = receive.isPending || startProcessing.isPending;

  const columns: GridColDef<DxSampleListDto>[] = [
    { field: "accessionNumber", headerName: "Accession", width: 180 },
    { field: "status", headerName: "Status", width: 130, renderCell: p => <StatusPill label={p.row.status} /> },
    { field: "collectionSeq", headerName: "Draw", width: 70, align: "right", headerAlign: "right",
      renderCell: p => p.row.recollectionOfSampleId
        ? <Tooltip title="Recollection"><span>#{p.row.collectionSeq}</span></Tooltip>
        : <span>#{p.row.collectionSeq}</span> },
    { field: "createdAt", headerName: "Collected", width: 180, valueGetter: v => v ? formatDateTime(v as string) : "" },
    {
      field: "actions", headerName: "", flex: 1, minWidth: 330, sortable: false, align: "right",
      renderCell: p => (
        <Stack direction="row" spacing={1}>
          <Button size="small" onClick={() => setDetailId(p.row.id)}>View</Button>
          {p.row.status === "Collected" && canCollect && (
            <Button size="small" variant="contained" disabled={busy} onClick={() => receive.mutate(p.row.id)}>Receive</Button>
          )}
          {p.row.status === "Received" && canCollect && (
            <Tooltip title="Put on the bench / analyzer — reports 'Pending' to HealNTrack">
              <span>
                <Button size="small" variant="contained" disabled={busy} onClick={() => startProcessing.mutate(p.row.id)}>
                  Start processing
                </Button>
              </span>
            </Tooltip>
          )}
          <Button size="small" variant="outlined" onClick={() => navigate(`/diagnostics/barcode?value=${encodeURIComponent(p.row.accessionNumber)}&sub=${p.row.id.slice(0, 8)}`)}>Barcode</Button>
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        breadcrumbs={["Diagnostics"]}
        title="Samples"
        subtitle="Accession, put under process, and track every specimen through to result."
      />
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <DiagnosticOrderIdField
            value={orderInput} onChange={setOrderInput} sx={{ minWidth: 320 }}
            onResolved={o => setOrderId(o?.id ?? null)}
          />
          <TextField select label="Status" size="small" value={status} onChange={e => setStatus(e.target.value)} sx={{ width: 200 }}>
            {STATUSES.map(s => <MenuItem key={s} value={s}>{s || "All"}</MenuItem>)}
          </TextField>
        </Stack>
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        {(receive.isError || startProcessing.isError) && (
          <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(receive.error ?? startProcessing.error)}</Alert>
        )}
        <Box sx={{ height: 560 }}>
          <DataGrid rows={query.data ?? []} columns={columns} loading={query.isLoading} getRowId={r => r.id}
            disableRowSelectionOnClick pageSizeOptions={[25, 50]} initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} />
        </Box>
      </Paper>
      <SampleDetailDialog id={detailId} onClose={() => setDetailId(null)} />
    </Box>
  );
}

function SampleDetailDialog({ id, onClose }: { id: string | null; onClose: () => void }) {
  const query = useQuery({ queryKey: ["dx-sample", id], queryFn: () => diagnosticsApi.getSample(id!), enabled: !!id });
  const s = query.data;
  return (
    <Dialog open={!!id} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Sample {s?.accessionNumber ?? ""}</DialogTitle>
      <DialogContent dividers>
        {query.isError && <Alert severity="error">{errorMessage(query.error)}</Alert>}
        {s && (
          <>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="body2">Status:</Typography><StatusPill label={s.status} />
            </Stack>
            {/* The full id — the one value the API needs and no grid column can sensibly show. */}
            <Typography variant="caption" color="text.secondary">Sample id: {s.id}</Typography>
            <Typography variant="subtitle2" sx={{ mt: 1 }}>Event ledger</Typography>
            <Table size="small">
              <TableHead><TableRow><TableCell>Event</TableCell><TableCell>Reason</TableCell><TableCell>Actor</TableCell><TableCell>When</TableCell></TableRow></TableHead>
              <TableBody>{s.events.map((e, i) => <TableRow key={i}><TableCell>{e.type}</TableCell><TableCell>{e.reason}</TableCell><TableCell>{e.actor}</TableCell><TableCell>{formatDateTime(e.occurredAt)}</TableCell></TableRow>)}</TableBody>
            </Table>
          </>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}
